import React, { useState } from "react";
import { LuPlus } from "react-icons/lu";
import { Button, cn } from "../../components/ui";
import { t } from "../../i18n";

export interface PickItem {
  id: number;
  name: string;
  detail?: string | null;
}

interface RecipientPickerProps {
  items: PickItem[];
  checkedIds: Set<number>;
  onToggle: (id: number) => void;
  /** Texto mostrado quando ainda não há nenhum vínculo. */
  emptyText: string;
  linkLabel: string;
  /** Formulário de vínculo; `done` fecha o formulário depois de vincular. */
  renderLinkForm: (done: () => void) => React.ReactNode;
  /** Item sem e-mail: fica desabilitado, com o link para completar o cadastro. */
  onCompleteEmail?: (id: number) => void;
}

/** Lista de hospitais, empresas ou reservas com checkbox, mais o "Vincular outro…". */
export const RecipientPicker: React.FC<RecipientPickerProps> = ({
  items,
  checkedIds,
  onToggle,
  emptyText,
  linkLabel,
  renderLinkForm,
  onCompleteEmail,
}) => {
  const [formOpen, setFormOpen] = useState(false);
  // Sem nenhum vínculo, o formulário já aparece aberto.
  const showForm = formOpen || items.length === 0;

  return (
    <div className="flex flex-col gap-2">
      {items.map((item) => {
        const disabled = Boolean(onCompleteEmail) && !item.detail;
        const checked = checkedIds.has(item.id);
        return (
          <label
            key={item.id}
            className={cn(
              "flex min-h-14 items-center gap-3 rounded-control border-strong px-3.5 py-3 transition-colors",
              disabled ? "border-dashed border-line" : "cursor-pointer hover:bg-primary-tint",
              checked ? "border-primary bg-primary-tint" : "border-line"
            )}
          >
            <input
              type="checkbox"
              checked={checked}
              disabled={disabled}
              onChange={() => onToggle(item.id)}
              aria-label={disabled ? t("sharing.email.withoutEmail", { name: item.name }) : undefined}
              className="h-5 w-5 shrink-0 accent-[rgb(var(--primary))] disabled:opacity-40"
            />
            <span className="flex min-w-0 flex-1 flex-col">
              <strong className="break-words text-label font-semibold text-fg">{item.name}</strong>
              {disabled ? (
                <button type="button" onClick={() => onCompleteEmail?.(item.id)} className="self-start text-caption font-semibold text-primary hover:underline">
                  {t("sharing.email.addCompanyEmail")}
                </button>
              ) : (
                item.detail && <span className="break-words text-caption text-fg-muted">{item.detail}</span>
              )}
            </span>
          </label>
        );
      })}

      {showForm ? (
        <div className="flex flex-col gap-2 rounded-control border-strong border-dashed border-primary-line bg-primary-tint p-3.5">
          {items.length === 0 && <p className="text-caption text-fg-muted">{emptyText}</p>}
          {renderLinkForm(() => setFormOpen(false))}
          {items.length > 0 && (
            <Button variant="text" size="sm" className="self-start" onClick={() => setFormOpen(false)}>
              {t("common.cancel")}
            </Button>
          )}
        </div>
      ) : (
        <Button variant="text" size="sm" className="self-start" icon={<LuPlus size={16} aria-hidden />} onClick={() => setFormOpen(true)}>
          {linkLabel}
        </Button>
      )}
    </div>
  );
};
